import { Component, computed, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { OrderPrestadorService } from 'src/app/services/order-prestador.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { OrdenPrestador } from 'src/app/models/OrdenPrestador';
import { CalificacionOrdenPrestador } from 'src/app/models/CalificacionOrdenPrestador';
import { API_URI } from 'src/environments/environment';
import { LoadingService } from 'src/app/services/loading.service';
import { firstValueFrom } from 'rxjs';

type Opcion = {
  valor: number;
  class: string;
  active: boolean;
  clicked: boolean;
};

@Component({
    selector: 'app-review-prestador',
    templateUrl: './review-prestador.component.html',
    styleUrls: ['./review-prestador.component.css'],
    standalone: false
})
export class ReviewPrestadorComponent {
  private _authService = inject(AuthService);
  private _orderPrestadorService = inject(OrderPrestadorService);
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private aRoute = inject(ActivatedRoute);
  private _location = inject(Location);

  error = signal<string>('');
  loadingGlobal = computed(() => this._loadingService.isLoading());
  loadingLocal = signal(false);

  id_orden_prestador: number;
  id_perfil_usuario: number;

  ordenPrestador?: OrdenPrestador;
  preview_img_avatar?: string;
  nombre?: string;

  calificacionOrdenPrestador?: CalificacionOrdenPrestador;

  n_experiencia = 0;
  n_pregunta_2 = 0;
  x_comentario = '';

  experiencias: Opcion[] = [];
  preguntas_2: Opcion[] = [];

  isFinished = signal(false);

  constructor() {
    this.id_orden_prestador = Number(this.aRoute.snapshot.paramMap.get('id_orden_prestador'));
    this.id_perfil_usuario = this._usuarioService.usuario().id_perfil ?? 0;

    this.experiencias = this.buildOpciones(0);
    this.preguntas_2 = this.buildOpciones(0);
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();

        if (this._authService.session() === 'guest') {
          this.router.navigate(['/sign-in']);
          return;
        }

        if (!this.id_orden_prestador) {
          this.router.navigate(['/dashboard']);
          return;
        }

        const [resCalOP, resOrden] = await Promise.all([
          firstValueFrom(this._orderPrestadorService.getCalificacionOrdenPrestadorXOrdenPrestador(this.id_orden_prestador)),
          firstValueFrom(this._orderPrestadorService.getOrdenPrestador(this.id_orden_prestador))
        ]);

        if (!resOrden) {
          this.error.set('La orden de prestador no existe o fue eliminada.');
          this.router.navigate(['/dashboard']);
          return;
        }

        this.ordenPrestador = resOrden;
        this.setHeader();

        if (resCalOP) {
          this.calificacionOrdenPrestador = resCalOP;

          this.n_experiencia = Number(resCalOP.n_experiencia) || 0;
          this.n_pregunta_2 = Number(resCalOP.n_pregunta_2) || 0;
          this.x_comentario = resCalOP.x_comentario ?? '';

          this.experiencias = this.buildOpciones(this.n_experiencia, true);
          this.preguntas_2 = this.buildOpciones(this.n_pregunta_2, true);
        }
      } catch (e: any) {
        console.error(e);

        if (e?.status === 401 || e?.status === 403) {
          this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
          this.router.navigate(['/sign-in']);
          return;
        }

        this.error.set(e?.error?.message || e?.message || 'No se pudo cargar la calificación.');
      }
    });
  }

  private setHeader(): void {
    if (!this.ordenPrestador) return;

    switch (this._usuarioService.usuario().id_perfil) {
      case 1: // ADMIN
      case 4: // COMERCIO -> ve cliente
        this.preview_img_avatar = this.ordenPrestador.imgAvatarCliente
          ? `${API_URI}uploads/images/large-${this.ordenPrestador.imgAvatarCliente}`
          : './assets/img/perfil-prestador-n.png';
        this.nombre = this.ordenPrestador.nombreCliente ?? '';
        break;

      case 2: // CLIENTE -> ve prestador
      case 3: // PRESTADOR -> ve prestador
      default:
        this.preview_img_avatar = this.ordenPrestador.imgAvatarPrestador
          ? `${API_URI}uploads/images/large-${this.ordenPrestador.imgAvatarPrestador}`
          : './assets/img/perfil-prestador-n.png';
        this.nombre = this.ordenPrestador.nombrePrestador ?? '';
        break;
    }
  }

  private getClase(index: number): string {
    if (index < 6) return 'option-low';
    if (index < 9) return 'option-medium';
    return 'option-high';
  }

  private buildOpciones(selected: number, lock: boolean = false): Opcion[] {
    const list: Opcion[] = [];
    for (let i = 1; i <= 10; i++) {
      const isOn = selected >= i;
      list.push({
        valor: i,
        class: this.getClase(i),
        active: isOn,
        clicked: lock ? isOn : isOn,
      });
    }
    return list;
  }

  calificarExpericencia(numero: number): void {
    if (this.calificacionOrdenPrestador) return;
    this.n_experiencia = numero;
    this.experiencias = this.buildOpciones(numero, false);
  }

  calificarPregunta2(numero: number): void {
    if (this.calificacionOrdenPrestador) return;
    this.n_pregunta_2 = numero;
    this.preguntas_2 = this.buildOpciones(numero, false);
  }

  hoverCalificacion(numero: number, tipo: 'experiencias' | 'preguntas_2'): void {
    if (this.calificacionOrdenPrestador) return;

    const lista = tipo === 'experiencias' ? this.experiencias : this.preguntas_2;

    lista.forEach((opcion) => {
      if (numero > 0) {
        opcion.active = opcion.valor <= numero;
      } else {
        if (tipo === 'experiencias') {
          opcion.active = opcion.valor <= this.n_experiencia;
        } else {
          opcion.active = opcion.valor <= this.n_pregunta_2;
        }
      }
    });
  }

  async finalizaReviewPrestador(): Promise<void> {
    this.error.set('');

    if (this.calificacionOrdenPrestador) return;

    if (!this.ordenPrestador) {
      this.error.set('No se pudo cargar el turno.');
      return;
    }

    if (this.n_experiencia === 0 || this.n_pregunta_2 === 0) {
      this.error.set('Debe calificar ambas opciones');
      return;
    }

    const payload: CalificacionOrdenPrestador = {
      id_usuario: this._usuarioService.usuario().id!,
      id_prestador: this.ordenPrestador.id_prestador!,
      id_orden_prestador: this.ordenPrestador.id!,
      n_experiencia: this.n_experiencia,
      n_pregunta_2: this.n_pregunta_2,
      x_comentario: (this.x_comentario ?? '').trim()
    };

    this.loadingLocal.set(true);

    try {
      await this._loadingService.run(async () => {
        await firstValueFrom(this._orderPrestadorService.createCalificacionOrdenPrestador(payload));
        // Estado CALIFICADO = 9
        await firstValueFrom(this._orderPrestadorService.updateEstadoOrdenPrestador(this.ordenPrestador!.id!, 9));
        this.isFinished.set(true);
      });
    } catch (e) {
      console.error(e);
      this.error.set('No se pudo enviar la calificación. Intente nuevamente.');
    } finally {
      this.loadingLocal.set(false);
    }
  }

  back(): void {
    this._location.back();
  }
}
